import React, { useMemo, useState } from 'react';
import {
  App,
  Button,
  Card,
  Checkbox,
  Descriptions,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import { ArrowLeftOutlined, DownloadOutlined, ReloadOutlined, SendOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { ENTITY_TYPE_DOCUMENT_COLLECTION } from '@/constants/approval-entity-types';
import {
  useBulkUpdateCollectionItems,
  useDocumentCollection,
  useRecollectDocuments,
  useSubmitDocumentCollection,
  useSyncCollectionUploads,
  useUpdateCollectionChecklist,
  useUpdateCollectionItem,
} from '@/hooks/useDocumentCollection';
import type { DocumentCollectionItemRow } from '@/services/document-collection.service';
import { documentService } from '@/services/document.service';
import documentCollectionService from '@/services/document-collection.service';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';

const { Text } = Typography;

const CATEGORIES = [
  'Customer PO',
  'RFQ / Inquiry',
  'Drawing',
  'Specification',
  'BOQ / Estimation',
  'Quotation',
  'Contract Review',
  'Work Order',
  'Inspection Report',
  'Dimension Report',
  'MTC',
  'Heat Traceability',
  'Test Certificate',
  'FAI / FIR',
  'TPI / Customer Witness',
  'NCR / Rework',
  'Delivery Document',
  'Photo Evidence',
  'Other',
];

const CHECKLIST_STATUSES = ['Available', 'NotRequired', 'Missing', 'Pending'];

const DocumentCollectionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>();
  const [includedOnly, setIncludedOnly] = useState(false);
  const { data, isLoading } = useDocumentCollection(id);
  const recollect = useRecollectDocuments();
  const syncUploads = useSyncCollectionUploads(id!);
  const bulkUpdate = useBulkUpdateCollectionItems(id!);
  const updateItem = useUpdateCollectionItem(id!);
  const updateChecklist = useUpdateCollectionChecklist(id!);
  const submit = useSubmitDocumentCollection();

  const request = data?.request;
  const items = data?.items ?? [];
  const checklist = data?.checklist ?? [];

  const filteredItems = useMemo(() => {
    let list = items;
    if (categoryFilter) list = list.filter((i) => i.category === categoryFilter);
    if (includedOnly) list = list.filter((i) => i.included);
    return list;
  }, [items, categoryFilter, includedOnly]);

  const canEdit = useMemo(() => {
    if (!request) return false;
    return ['Assigned', 'Under Review', 'Returned for Correction', 'Auto-Collected'].includes(request.status);
  }, [request]);

  const itemColumns: ColumnsType<DocumentCollectionItemRow> = [
    {
      title: 'Include',
      width: 70,
      render: (_, r) => (
        <Checkbox
          checked={r.included}
          disabled={!canEdit || updateItem.isPending}
          onChange={(e) => updateItem.mutate({ itemId: r.id, body: { included: e.target.checked } })}
        />
      ),
    },
    { title: 'Category', dataIndex: 'category', width: 140 },
    {
      title: 'File',
      key: 'file',
      render: (_, r) => r.document?.document_name || r.document?.file_name || '—',
    },
    { title: 'Source', dataIndex: 'source_entity_type', width: 120 },
    { title: 'Doc No', dataIndex: 'source_document_number', width: 110, render: (v) => v || '—' },
    {
      title: 'Uploaded',
      key: 'up',
      width: 110,
      render: (_, r) =>
        r.document?.created_at ? dayjs(r.document.created_at).format('DD MMM YY') : '—',
    },
    {
      title: '',
      key: 'act',
      width: 100,
      render: (_, r) =>
        r.document?.id ? (
          <Button size="small" onClick={() => documentService.viewDocument(r.document!.id)}>
            View
          </Button>
        ) : null,
    },
    {
      title: 'Category edit',
      key: 'cat',
      width: 160,
      render: (_, r) =>
        canEdit ? (
          <Select
            size="small"
            style={{ width: '100%' }}
            value={r.category}
            options={CATEGORIES.map((c) => ({ value: c, label: c }))}
            onChange={(v) => updateItem.mutate({ itemId: r.id, body: { category: v } })}
          />
        ) : (
          r.category
        ),
    },
  ];

  if (!id) {
    navigate('/growth/document-collections');
    return null;
  }

  if (isLoading || !request) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Document Collection"
          documentNumber={request.collection_number}
          subtitle={
            <Space>
              <Tag>{request.status}</Tag>
              {request.deliveryNote?.delivery_note_number && (
                <Button
                  type="link"
                  size="small"
                  onClick={() => navigate(`/growth/delivery-notes/${request.delivery_note_id}`)}
                >
                  DN {request.deliveryNote.delivery_note_number}
                </Button>
              )}
            </Space>
          }
          primaryActions={
            <Space wrap>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/growth/document-collections')}>
                Back
              </Button>
              {canEdit && (
                <Button
                  icon={<ReloadOutlined />}
                  loading={recollect.isPending}
                  onClick={() =>
                    recollect.mutate(id, {
                      onSuccess: (d) => message.success(`Added ${d.added} document(s)`),
                    })
                  }
                >
                  Re-collect
                </Button>
              )}
              {canEdit && (
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  loading={submit.isPending}
                  onClick={() =>
                    submit.mutate(id, {
                      onSuccess: () => message.success('Submitted for approval'),
                    })
                  }
                >
                  Submit for approval
                </Button>
              )}
              <Button
                onClick={async () => {
                  try {
                    const data = await documentCollectionService.getTransmittal(id);
                    const w = window.open('', '_blank');
                    if (!w) return;
                    const rows = (data.documents || [])
                      .map(
                        (d) =>
                          `<tr><td>${d.category}</td><td>${d.file_name || ''}</td><td>${d.source_document_number || ''}</td></tr>`
                      )
                      .join('');
                    w.document.write(
                      `<html><head><title>Transmittal ${data.collection_number}</title></head><body>
                      <h2>Document Transmittal</h2>
                      <p>Customer: ${data.customer_name || ''}</p>
                      <p>SO: ${data.sales_order_number || ''} | DN: ${data.delivery_note_number || ''}</p>
                      <table border="1" cellpadding="6"><tr><th>Category</th><th>File</th><th>Source</th></tr>${rows}</table>
                      </body></html>`
                    );
                    w.document.close();
                    w.print();
                  } catch {
                    message.error('Transmittal failed');
                  }
                }}
              >
                Print transmittal
              </Button>
              <ReportDocumentActions
                reportKey="document-collection-transmittal"
                params={{ id: request.id }}
                documentId={request.id}
                sendPermissions={['growth.document_collection.edit']}
                emailLabel="Email customer"
              />
              <Button
                icon={<DownloadOutlined />}
                onClick={async () => {
                  try {
                    const blob = await documentCollectionService.downloadZip(id);
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${request.collection_number}-package.zip`;
                    a.click();
                    window.URL.revokeObjectURL(url);
                  } catch {
                    message.error('ZIP download failed');
                  }
                }}
              >
                Download ZIP
              </Button>
            </Space>
          }
        />
      }
    >
      <Descriptions bordered size="small" column={3} style={{ marginBottom: 16 }}>
        <Descriptions.Item label="Customer">{request.customer?.customer_name ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="Delivery date">
          {request.delivery_date ? dayjs(request.delivery_date).format('DD MMM YYYY') : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Priority">{request.priority}</Descriptions.Item>
      </Descriptions>

      <Tabs
        items={[
          {
            key: 'docs',
            label: `Documents (${items.length})`,
            children: (
              <Card>
                {items.length === 0 && (
                  <Text type="warning" style={{ display: 'block', marginBottom: 12 }}>
                    No documents collected yet. Use Re-collect or add attachments on the linked Opportunity,
                    Quotation, Customer Order, Work Order, GRN, etc.
                  </Text>
                )}
                <Space style={{ marginBottom: 12 }} wrap>
                  <Select
                    allowClear
                    placeholder="Filter category"
                    style={{ width: 200 }}
                    value={categoryFilter}
                    onChange={setCategoryFilter}
                    options={CATEGORIES.map((c) => ({ value: c, label: c }))}
                  />
                  <Checkbox checked={includedOnly} onChange={(e) => setIncludedOnly(e.target.checked)}>
                    Included only
                  </Checkbox>
                  <Text type="secondary">{items.filter((i) => i.included).length} selected</Text>
                  {canEdit && items.length > 0 && (
                    <>
                      <Button
                        size="small"
                        loading={bulkUpdate.isPending}
                        onClick={() =>
                          bulkUpdate.mutate(
                            { included: true, all: true },
                            { onSuccess: (d) => message.success(`Selected ${d.updated} document(s)`) }
                          )
                        }
                      >
                        Select all
                      </Button>
                      <Button
                        size="small"
                        loading={bulkUpdate.isPending}
                        onClick={() =>
                          bulkUpdate.mutate(
                            { included: false, all: true },
                            { onSuccess: (d) => message.success(`Cleared ${d.updated} document(s)`) }
                          )
                        }
                      >
                        Clear all
                      </Button>
                    </>
                  )}
                </Space>
                <Table rowKey="id" size="small" columns={itemColumns} dataSource={filteredItems} pagination={{ pageSize: 15 }} />
              </Card>
            ),
          },
          {
            key: 'checklist',
            label: 'Required checklist',
            children: (
              <Table
                rowKey="id"
                size="small"
                dataSource={checklist}
                pagination={false}
                columns={[
                  { title: 'Item', dataIndex: 'checklist_label' },
                  {
                    title: 'Status',
                    dataIndex: 'status',
                    render: (v, r) =>
                      canEdit ? (
                        <Select
                          size="small"
                          style={{ width: 140 }}
                          value={v}
                          options={CHECKLIST_STATUSES.map((s) => ({ value: s, label: s }))}
                          onChange={(st) => updateChecklist.mutate({ checklistId: r.id, body: { status: st } })}
                        />
                      ) : (
                        <Tag>{v}</Tag>
                      ),
                  },
                  {
                    title: 'Blocks approval',
                    dataIndex: 'blocks_approval',
                    render: (v) => (v ? <Tag color="red">Yes</Tag> : 'No'),
                  },
                ]}
              />
            ),
          },
          {
            key: 'upload',
            label: 'Additional uploads',
            children: (
              <EntityAttachmentsCard
                entityType="DocumentCollectionRequest"
                entityId={id}
                title="Additional documents"
                readOnly={!canEdit}
                onDocumentsChanged={() => {
                  if (!canEdit) return;
                  syncUploads.mutate(undefined, {
                    onSuccess: (d) => {
                      if (d.added > 0) message.success(`Added ${d.added} document(s) to the package list`);
                    },
                  });
                }}
              />
            ),
          },
          {
            key: 'approval',
            label: 'Approval',
            children: request.approval_instance_id ? (
              <ApprovalPanel
                entityType={ENTITY_TYPE_DOCUMENT_COLLECTION}
                entityId={id}
                extraInvalidateQueryKeys={[['document-collections', 'detail', id]]}
              />
            ) : (
              <Text type="secondary">Not submitted for approval yet.</Text>
            ),
          },
        ]}
      />
    </WorkspaceLayout>
  );
};

export default DocumentCollectionDetailPage;
